import styled, { css } from 'styled-components';
import { Modal as AntdModal } from 'antd';

/* Video panel inside the offer card, in the same dark language as the card around
   it so it reads as part of the same screen. */

/* A supporting piece, not primary content. No width cap: it follows the column
   containing it. At a fixed 280px it ended up narrower than the offer card right
   above it in SignDeal's aside. */
export const Panel = styled.aside<{ $flat?: boolean; $row?: boolean }>`
  font-family: 'Inter', sans-serif;
  overflow: hidden;

  /* Frame left, text right: far shorter in a wide card. Below 520px it stacks
     again, otherwise the frame gets too small. */
  ${({ $row }) =>
    $row &&
    css`
      display: flex;
      align-items: center;
      gap: 16px;

      @media (max-width: 520px) {
        display: block;
      }
    `}

  ${({ $flat }) =>
    $flat
      ? css`
          /* Inside a card: the frame keeps its radius, the rest has no box. */
          border-radius: 0;
        `
      : css`
          border: 1px solid rgba(255, 255, 255, 0.12);
          /* 16px = --radius-lg, the same as the flow's cards. */
          border-radius: 16px;
          background: rgba(255, 255, 255, 0.04);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
        `}

  /* Tracks the top of the offer card on wide screens; stacks below on narrow ones,
     where "beside" doesn't exist. */
  @media (min-width: 1200px) {
    position: sticky;
    top: 24px;
  }

  @media (max-width: 1199px) {
    margin: 32px auto 0;
  }
`;

/* No side inset when flat: the surrounding card already provides the spacing. */
export const Footer = styled.div<{ $flat?: boolean; $row?: boolean }>`
  ${({ $flat, $row }) => css`
    padding: ${$flat ? '14px 0 0' : '14px 18px 18px'};

    ${$row &&
    css`
      flex: 1;
      min-width: 0;
      padding: 0;

      @media (max-width: 520px) {
        padding: 14px 0 0;
      }
    `}
  `}
`;

export const Title = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.3;
  color: #ffffff;
`;

/* Fixed 16:9 ratio: without it the frame changes height when the poster is swapped
   for the iframe, and the whole column jumps. */
export const Frame = styled.div<{ $poster?: string; $row?: boolean }>`
  ${({ $poster, $row }) => css`
    position: relative;
    aspect-ratio: 16 / 9;
    border-radius: 12px;
    overflow: hidden;
    background-color: #1c1930;

    ${$row &&
    css`
      flex: 0 0 200px;
      width: 200px;

      @media (max-width: 520px) {
        flex: none;
        width: 100%;
      }
    `}
    ${$poster &&
    css`
      background-image: url(${$poster});
      background-size: cover;
      background-position: center;
    `}

    display: flex;
    align-items: center;
    justify-content: center;

    iframe {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      border: none;
    }
  `}
`;

/* Dark veil over the poster so the play button keeps its contrast regardless of
   the image frame. */
export const Veil = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(18, 18, 18, 0.15) 0%,
    rgba(18, 18, 18, 0.45) 100%
  );
`;

/* The design system's play button comes in the brand pink, the most saturated
   colour on the screen, which made the video look like the main subject. Here it is
   supporting: a translucent white circle with a dark icon, which reads as "there is
   a video" without competing with the CTA. */
export const PlayLayer = styled.div`
  position: relative;
  z-index: 1;

  button {
    background: rgba(255, 255, 255, 0.92) !important;
    border-color: transparent !important;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  }

  button:hover {
    background: #ffffff !important;
  }

  svg {
    color: #121212 !important;
    fill: #121212 !important;
  }
`;

export const Caption = styled.p`
  margin: 4px 0 0;
  font-size: 1.2rem;
  line-height: 1.5;
  color: #bebebe;
`;

/* The video plays in a modal rather than inside the card, so the frame is not
   capped by the column it sits in.

   10px of gutter on each side keeps it off the viewport edges on narrow screens,
   and the body carries the same 10px so the player has a thin, even frame instead
   of butting against the modal corners. */
export const MODAL_SIDE_PADDING = 10;

export const Modal = styled(AntdModal)`
  max-width: calc(100vw - ${MODAL_SIDE_PADDING * 2}px);

  .ant-modal-content {
    background: #13131f;
    border-radius: 12px;
    overflow: hidden;
  }

  .ant-modal-body {
    padding: ${MODAL_SIDE_PADDING}px;
    line-height: 0;
  }

  .ant-modal-close {
    color: rgba(255, 255, 255, 0.75);

    &:hover {
      color: #ffffff;
    }
  }
`;

/* 16:9 so the modal keeps its shape while the embed loads, instead of collapsing
   and then jumping to height. */
export const ModalFrame = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  width: 100%;

  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;
